import { Injectable } from '@angular/core';
import { UtilsService } from './utils/utils.service';
import { TYPE_TOAST } from '../models/enumeraciones';
import {
	GoToPreviousStep,
	GoToNextStep,
	GoToRejectedStep,
} from '../models/utils/steps';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
	selectCanCurrentStepRouteProcessGoBack,
	selectCurrentProcess,
	selectIsThereNextStepRoute,
	selectIsTherePreviousStepRoute,
	selectPathFor,
} from '../state/selectors/onboarding-process.selectors';
import {
	catchError,
	forkJoin,
	Observable,
	of,
	ReplaySubject,
	Subject,
	take,
	zip,
} from 'rxjs';
import { OnboardingProcessActions } from '../state/actions/onboarding-process.actions';
import {
	selectIsApplicantAMember,
	selectProductInfo,
	selectPreAplicationState,
} from '../state/selectors/pre-application.selectors';
import { RoutingActions } from '../state/actions/routing.actions';
import { ROUTES } from '../data/routing-navigation.data';
import { OnboardingService } from './apis/onboarding.service';
import { Process } from '../models/state/onboarding-process-state.model';
import {
	mapFromDigitalProcessList,
	mapFromStepRouteObject,
} from '../models/utils/mappers/onboarding-process.mapper';
import { DeclinedMessageActions } from '../state/actions/declined-message.actions';
import { PreApplicationActions } from '../state/actions/pre-application.actions';
import {
	setOfferData,
	setCurpFlowIdentity,
} from '../state/actions/preauthorized-offer.actions';
import {
	Data,
	DigitalProcessResponse,
} from '../models/apis/digital-process.model';
import {
	getApplicantType,
	mapFromPreApplicationStateFrom,
} from '../models/utils/mappers/pre-application.mapper';
import { OtpReauthenticationService } from './auth/otp-reauthentication.service';
import { LocalStorageService } from './utils/local-storage.service';
import { FullSizeLoadingService } from './utils/full-size-loading.service';
import {
	FullProcessResponse,
	ProductResponse,
} from '../models/apis/onboarding-process.model';
import {
	ApplicantType,
	PreApplicationState,
} from '../models/state/pre-application.model';
import { mapFromOfferResponse } from '../models/utils/mappers/offer-response.mapper';
import { PreauthorizedOffer } from '../models/state/preauthorized-offer.model';
import { OfferResponse } from '../models/apis/offer.model';
import { HttpErrorResponse } from '@angular/common/http';


const HOME_ROUTES: ReadonlyArray<string> = [
	ROUTES.HOME,
	ROUTES.HOME_INVESTMENT,
	ROUTES.HOME_TDC,
	ROUTES.HOME_PREAUTHORIZED,
];

@Injectable({
	providedIn: 'root',
})
export class OnboardingProcessFlowService
	implements GoToNextStep, GoToPreviousStep, GoToRejectedStep, GoToRejectedStep
{
	private static readonly OFFER_PRODUCT_CODE_KEY = 'offer_product_code';
	private _isChangingStep: boolean = false;
	private _offerProductCode: string | null = null;

	constructor(
		private store: Store,
		private router: Router,
		private onboardingService: OnboardingService,
		private localStorageService: LocalStorageService,
		private utilsService: UtilsService,
		private fullSizeLoadingService: FullSizeLoadingService,
		private otpReauthentication: OtpReauthenticationService
	) {}

	get isChangingStep(): boolean {
		return this._isChangingStep;
	}

	changingStepCompleted(): void {
		this._isChangingStep = false;
	}

	/**
	 * El observable emite si el paso realmente cambió. Se ejecuta de inmediato,
	 * así que quien no lo necesite puede ignorarlo; quien deshabilite un botón
	 * mientras avanza debe usarlo para volver a habilitarlo cuando no hubo
	 * navegación.
	 */
	goToNextStep(): Observable<boolean> {
		if (this._isChangingStep) {
			return of(false);
		}
		this.startChangingStep();
		const hasStepChanged$ = new ReplaySubject<boolean>(1);
		zip([
			this.store.select(selectIsApplicantAMember).pipe(take(1)),
			this.store.select(selectIsThereNextStepRoute).pipe(take(1)),
		]).subscribe({
			next: result => {
				const [isApplicantAMember, isThereNextStepRoute] = result;
				if (!isThereNextStepRoute) {
					this.abortChangingStep(
						'No hay una siguiente ruta en el proceso',
						hasStepChanged$
					);
					return;
				}
				this.store.dispatch(
					OnboardingProcessActions.goNextProcess({
						canSkipNextProcess: isApplicantAMember,
					})
				);
				this.updateCurrentRouteAndNavigateToStep(hasStepChanged$);
			},
		});
		return hasStepChanged$.asObservable();
	}

	goToPreviousStep(): Observable<boolean> {
		if (this._isChangingStep) {
			return of(false);
		}
		this.startChangingStep();
		const hasStepChanged$ = new ReplaySubject<boolean>(1);
		zip([
			this.store.select(selectCanCurrentStepRouteProcessGoBack).pipe(take(1)),
			this.store.select(selectIsApplicantAMember).pipe(take(1)),
			this.store.select(selectIsTherePreviousStepRoute).pipe(take(1)),
		]).subscribe({
			next: result => {
				const [
					canCurrentStepRouteProcessGoBack,
					isApplicantAMember,
					isTherePreviousStepRoute,
				] = result;
				if (!canCurrentStepRouteProcessGoBack || !isTherePreviousStepRoute) {
					this.abortChangingStep(
						'El paso actual no permite retroceder o no hay ruta previa',
						hasStepChanged$
					);
					return;
				}
				this.store.dispatch(
					OnboardingProcessActions.goPreviousProcess({
						canSkipNextProcess: isApplicantAMember,
					})
				);
				this.updateCurrentRouteAndNavigateToStep(hasStepChanged$);
			},
		});
		return hasStepChanged$.asObservable();
	}

	goToRejected(message: string = null, code: string = null): void {
		if (this._isChangingStep) {
			return;
		}
		// Mientras el socio está capturando un OTP nuevo, las peticiones que se
		// descartaron por el token vencido siguen resolviendo su `error`, y varias
		// pantallas rechazan ahí. Ese rechazo pisaría la navegación a la pantalla
		// de verificación y mataría un proceso que sí es recuperable.
		// AuthRecoveryService.goToRejectedByPermissions cancela la reautenticación
		// antes de llamar aquí, así que un rechazo real de permisos sí pasa.
		if (this.otpReauthentication.isPending) {
			console.warn(
				'[Onboarding] Rechazo omitido: hay una reautenticación por OTP en curso'
			);
			return;
		}
		this.startChangingStep();
		// Al ir a la pantalla de oferta declinada siempre se detiene cualquier
		// carga en curso (p. ej. el loading de rutas_proceso al recibir 404),
		// para que no quede el overlay encima de declined-offer.
		this.fullSizeLoadingService.close();
		const declinedPath = ROUTES.DECLINED_OFFER;
		this.store.dispatch(
			DeclinedMessageActions.addDeclinedCodeAndMessage({
				code,
				message,
			})
		);
		this.updateCurrentRoute(declinedPath);
		this.navigateTo(declinedPath).then(
			hasNavigated => {
				if (!hasNavigated) {
					this.abortChangingStep(
						`La navegación a ${declinedPath} no se completó`
					);
				}
			},
			(error: Error) => {
				this.abortChangingStep(
					`Error al navegar a ${declinedPath}: ${error?.message}`
				);
			}
		);
	}

	goFirtsStep(): void {
		if (this._isChangingStep) {
			return;
		}
		this.startChangingStep();
		this.updateCurrentRouteAndNavigateToStep();
	}

	get offerProductCode(): string | null {
		if (!this._offerProductCode) {
			this._offerProductCode = this.localStorageService.get<string>(
				OnboardingProcessFlowService.OFFER_PRODUCT_CODE_KEY
			);
		}
		return this._offerProductCode;
	}

	loadOnboardingProgress(productId: number): Observable<void> {
		return new Observable(observer => {
			// getProcessProduct ya no se utiliza — los pasos llegan por getDigitalProcess
			// this.onboardingService.getProcessProduct(productId).subscribe({ ... })
			this.onboardingService.getProductBy(productId).subscribe({
				next: product => {
					this.addProductToStore(product);
					this.addTotalStepsToStore(product.total_pasos);
					observer.next();
					observer.complete();
				},
				error: (error: Error) => {
					console.log(
						'🚀 ~ file: onboarding-process-flow.service.ts ~ getProductBy ~ error:',
						error
					);
					observer.error(error.message);
				},
			});
		});
	}

	loadOnboardingProgressFromDigitalProcess(
		preApplicationId: number
	): Observable<void> {
		return new Observable(observer => {
			this.onboardingService
				.getDigitalProcess({ idPresolicitud: preApplicationId })
				.subscribe({
					next: (digitalProcess: DigitalProcessResponse) => {
						if (digitalProcess?.process.length === 0) {
							observer.error('processes is empty');
							observer.complete();
							return;
						}

						this.store.dispatch(
							OnboardingProcessActions.updateOnboardingProgress({
								processes: mapFromDigitalProcessList(digitalProcess.process),
								canSkipStepNextStep:
									getApplicantType(digitalProcess.data[0].id_tipo_persona) ===
									ApplicantType.MEMBER,
							})
						);
						this.loadPreApplicationStateFromDigitalProcess(
							digitalProcess.data[0]
						);
						const productId = digitalProcess.process[0].id_producto;
						this.onboardingService.getProductBy(productId).subscribe({
							next: product => {
								this.addProductToStore(product);
								this.addTotalStepsToStore(product.total_pasos);
								this.utilsService.ShowToast(
									'Oferta preautorizada no encontrada, cargando producto',
									TYPE_TOAST.INFO
								);
								observer.next();
								observer.complete();
							},
							error: (error: Error) => {
								console.log(
									'🚀 ~ file: onboarding-process-flow.service.ts:167 ~ this.onboardingService.getProductBy ~ error:',
									error
								);
								observer.error(error.message);
								observer.complete();
							},
						});
					},
					error: (error: Error) => {
						console.log(
							'🚀 ~ file: onboarding-process-flow.service.ts:123 ~ error:',
							error
						);
						observer.error(error.message);
						observer.complete();
					},
				});
		});
	}

	canCurrentStepGoBack(): Observable<boolean> {
		return this.store
			.select(selectCanCurrentStepRouteProcessGoBack)
			.pipe(take(1));
	}

	private addTotalStepsToStore(totalSteps: number) {
		this.store.dispatch(
			OnboardingProcessActions.addTotalSteps({
				totalSteps,
			})
		);
	}

	private addProductToStore(product: ProductResponse) {
		this.store.dispatch(
			PreApplicationActions.addProductData({
				productId: product.id_producto,
				subProductId: product.id_subproducto,
				productName: product.descripcion,
				segmentId: product.id_segmento,
			})
		);
	}

	loadOnboardingProgressWithDigitalProcess(
		digitalProcess: DigitalProcessResponse
	): Observable<void> {
		return new Observable(observer => {
			if (digitalProcess.process.length === 0) {
				observer.error('processes is empty');
				return;
			}
			this.store.dispatch(
				OnboardingProcessActions.updateOnboardingProgress({
					processes: mapFromDigitalProcessList(digitalProcess.process),
					canSkipStepNextStep:
						getApplicantType(digitalProcess.data[0].id_tipo_persona) ===
						ApplicantType.MEMBER,
				})
			);
			this.loadPreApplicationStateFromDigitalProcess(digitalProcess.data[0]);
			const productId = digitalProcess.process[0].id_producto;
			this.onboardingService.getProductBy(productId).subscribe({
				next: product => {
					this.addProductToStore(product);
					this.addTotalStepsToStore(product.total_pasos);
					observer.next();
					return;
				},
				error: (error: Error) => {
					console.log(
						'🚀 ~ file: onboarding-process-flow.service.ts:244 ~ this.onboardingService.getProductBy ~ error:',
						error
					);
					observer.error(error.message);
					return;
				},
			});
		});
	}

	loadPreApplicationStateFromDigitalProcess(data: Data) {
		this.store.dispatch(
			PreApplicationActions.loadPreApplicationDataFromDigitalProcess(
				mapFromPreApplicationStateFrom(data)
			)
		);
	}

	hasProcessInProgress(curp: string): Observable<boolean> {
		return new Observable(observer => {
			this.store
				.select(selectProductInfo)
				.pipe(take(1))
				.subscribe(productInfo => {
					this.onboardingService
						.getDigitalProcess({
							curp: curp,
							idProducto: productInfo.productId,
						})
						.subscribe({
							next: digitalProcess => {
								const { process } = digitalProcess;
								const hasAProcessInProgress =
									process.length > 0 &&
									process[1].id_paso > 1 &&
									process.every(
										digitalProcess =>
											digitalProcess.ruta !== ROUTES.PERSONAL_DATA
									);
								if (hasAProcessInProgress) {
									this.loadOnboardingProgressWithDigitalProcess(
										digitalProcess
									).subscribe({
										next: () => observer.next(hasAProcessInProgress),
										error: (error: Error) => observer.error(error),
									});
									return;
								}
								observer.next(hasAProcessInProgress);
							},
							error: (error: Error) => {
								observer.error(error);
							},
						});
				});
		});
	}

	loadOnboardingProgressFromOffer(
		productCode: string,
		memberNumber?: number,
		includeOfferData: boolean = true
	): Observable<void> {
		const normalizedProductCode = productCode?.toUpperCase();
		this._offerProductCode = normalizedProductCode || this._offerProductCode;
		if (this._offerProductCode) {
			this.localStorageService.save<string>(
				OnboardingProcessFlowService.OFFER_PRODUCT_CODE_KEY,
				this._offerProductCode
			);
		}
		const hasMemberNumber =
			typeof memberNumber === 'number' && !Number.isNaN(memberNumber);
		const shouldLoadOfferData = includeOfferData && hasMemberNumber;
		return new Observable(observer => {
			forkJoin({
				processes: this.onboardingService.getOfferRoutes(
					normalizedProductCode,
					memberNumber
				),
				offer: shouldLoadOfferData
					? this.onboardingService.getOfferByProduct(
							memberNumber,
							normalizedProductCode
					  )
					: of(null),
				initialInformation: hasMemberNumber
					? this.onboardingService.getInitialInformation(
							normalizedProductCode,
							memberNumber
					  ).pipe(catchError(() => of(null)))
					: of(null),
			}).subscribe({
				next: ({ processes, offer, initialInformation }) => {
					if (!processes || processes.length === 0) {
						observer.error('No se encontraron rutas para este producto.');
						observer.complete();
						return;
					}

					this.store.dispatch(
						OnboardingProcessActions.updateOnboardingProgress({
							processes: mapFromDigitalProcessList(processes),
							canSkipStepNextStep: true,
						})
					);
					if (shouldLoadOfferData && offer && initialInformation) {
						const memberName = initialInformation?.nombre_socio ?? '';
						const preApplicationId = initialInformation?.id_presolicitud ?? 0;
						const applicationId = initialInformation?.id_solicitud ?? 0;
						const offerData: PreauthorizedOffer = mapFromOfferResponse(offer, {
							productCode: normalizedProductCode,
							memberId: memberNumber,
							memberName,
						});
						this.store.dispatch(
							setOfferData({
								offer: offerData,
							})
						);
						this.updatePreApplicationStateFromOffer(
							normalizedProductCode,
							memberNumber,
							memberName,
							offer,
							preApplicationId,
							applicationId,
							processes[0]?.id_producto
						);
					} else if (hasMemberNumber && !shouldLoadOfferData) {
						// Flujo CURP: poblar los campos críticos de identidad/producto en el store
						this.store.dispatch(
							setCurpFlowIdentity({
								offerPersonId: memberNumber,
								productCobisId: normalizedProductCode,
								folioDigital: normalizedProductCode,
								isApplicantAMember: true,
							})
						);
						// Poblar applicantId en preApplicationState para que offer-confirmation pueda ejecutar sus endpoints
						this.store
							.select(selectPreAplicationState)
							.pipe(take(1))
							.subscribe(currentState => {
								this.store.dispatch(
									PreApplicationActions.loadPreApplicationDataFromDigitalProcess({
										...currentState,
										applicantId: memberNumber,
										isApplicantAMember: true,
										...(initialInformation?.id_presolicitud != null && {
											preApplicationId: initialInformation.id_presolicitud,
										}),
									})
								);
							});
						const hasPersonalData = processes.some(
							p => p.ruta === ROUTES.PERSONAL_DATA
						);
						const hasHomeStep = processes.some(p =>
							HOME_ROUTES.includes(p.ruta)
						);
						if (hasPersonalData && hasHomeStep) {
							this.store.dispatch(
								OnboardingProcessActions.goNextProcess({
									canSkipNextProcess: false,
								})
							);
						}
					}
					const product = {
						id_producto: processes[0]?.id_producto ?? 1,
						nombre_icono: '',
						descripcion: '',
						activo: true,
						id_subproducto: 245,
						total_pasos: processes.length,
						id_segmento: 94,
					};
					this.addProductToStore(product);
					this.addTotalStepsToStore(product.total_pasos);
					observer.next();
					observer.complete();
				},
				error: (error: Error | HttpErrorResponse) => {
					if (error instanceof HttpErrorResponse && error.status === 404) {
						this.goToRejected(
							'No fue posible continuar con su solicitud. Para más información, comuníquese con nuestros canales de atención o visite una de nuestras sucursales.'
						);
						observer.complete();
						return;
					}
					if (hasMemberNumber) {
						// Fallback: alguno de los endpoints falló (ej. ofertasMotor).
						// Se reintenta solo con getOfferRoutes sin id_persona (como si no se hubiera proporcionado num).
						console.warn(
							'Error al cargar datos con num, usando fallback sin id_persona:',
							error
						);
						this.onboardingService
							.getOfferRoutes(normalizedProductCode, undefined)
							.subscribe({
								next: processes => {
									if (!processes || processes.length === 0) {
										observer.error(
											'No se encontraron rutas para este producto.'
										);
										observer.complete();
										return;
									}
									this.store.dispatch(
										OnboardingProcessActions.updateOnboardingProgress({
											processes: mapFromDigitalProcessList(processes),
											canSkipStepNextStep: true,
										})
									);
									const product = {
										id_producto: processes[0]?.id_producto ?? 1,
										nombre_icono: '',
										descripcion: '',
										activo: true,
										id_subproducto: 245,
										total_pasos: processes.length,
										id_segmento: 94,
									};
									this.addProductToStore(product);
									this.addTotalStepsToStore(product.total_pasos);
									this.utilsService.ShowToast(
										'Oferta preautorizada no encontrada, cargando producto',
										TYPE_TOAST.INFO
									);
									observer.next();
									observer.complete();
								},
								error: (fallbackError: Error | HttpErrorResponse) => {
									if (
										fallbackError instanceof HttpErrorResponse &&
										fallbackError.status === 404
									) {
										this.goToRejected(
											'No fue posible continuar con su solicitud. Para más información, comuníquese con nuestros canales de atención o visite una de nuestras sucursales.'
										);
										observer.complete();
										return;
									}
									console.error(
										'Error al cargar datos del onboarding (fallback):',
										fallbackError
									);
									observer.error(fallbackError.message);
									observer.complete();
								},
							});
					} else {
						console.error('Error al cargar datos del onboarding:', error);
						observer.error(error.message);
						observer.complete();
					}
				},
			});
		});
	}

	private navigateTo(path: string): Promise<boolean> {
		return this.router.navigateByUrl(path);
	}

	private updatePreApplicationStateFromOffer(
		productCode: string,
		memberNumber: number,
		memberName: string,
		offer: OfferResponse,
		preApplicationId: number,
		applicationId: number,
		productIdFromProcess?: number
	) {
		this.store
			.select(selectPreAplicationState)
			.pipe(take(1))
			.subscribe(currentState => {
				const updatedState: PreApplicationState = {
					...currentState,
					applicantId: memberNumber,
					applicantName: {
						name: memberName ?? '',
						firstSurname: '',
						secondSurname: '',
					},
					applicantType: ApplicantType.MEMBER,
					isApplicantAMember: true,
					authorizedAmount: offer?.monto ?? currentState.authorizedAmount,
					amount: offer?.monto ?? currentState.amount,
					rate: offer?.tasa ?? currentState.rate,

					creditTerm:
						currentState.creditTerm > 0
							? currentState.creditTerm
							: offer?.plazo ?? 0,
					applicantAmountSelected:
						currentState.applicantAmountSelected || offer?.monto || 0,
					folio: productCode,
					productCobisId: productCode,
					preApplicationId: preApplicationId ?? currentState.preApplicationId,
					applicationId: applicationId ?? currentState.applicationId,
					productId:
						productIdFromProcess && productIdFromProcess > 0
							? productIdFromProcess
							: currentState.productId,
					isCreditApproved: true,
				};
				this.store.dispatch(
					PreApplicationActions.loadPreApplicationDataFromDigitalProcess(
						updatedState
					)
				);
			});
	}

	private updateCurrentRouteAndNavigateToStep(
		hasStepChanged$?: Subject<boolean>
	) {
		this.store
			.select(selectCurrentProcess)
			.pipe(take(1))
			.subscribe({
				next: currentProcess => {
					if (!currentProcess) {
						this.abortChangingStep(
							'No hay un proceso actual al cual navegar',
							hasStepChanged$
						);
						return;
					}
					this.getPathOfStepAndNavigate(currentProcess, hasStepChanged$);
				},
			});
	}

	private getPathOfStepAndNavigate(
		process: Process,
		hasStepChanged$?: Subject<boolean>
	) {
		this.store
			.select(selectPathFor(process.stepNumber, process.stepRouteNumber))
			.pipe(take(1))
			.subscribe({
				next: currentRoute => {
					if (!currentRoute) {
						this.abortChangingStep(
							`No hay ruta para el paso ${process.stepNumber} con orden ${process.stepRouteNumber}`,
							hasStepChanged$
						);
						return;
					}
					this.updateCurrentRoute(currentRoute);
					this.navigateTo(currentRoute).then(
						hasNavigated => {
							if (!hasNavigated) {
								this.abortChangingStep(
									`La navegación a ${currentRoute} no se completó`,
									hasStepChanged$
								);
								return;
							}
							this.notifyStepChange(hasStepChanged$, true);
						},
						(error: Error) => {
							this.abortChangingStep(
								`Error al navegar a ${currentRoute}: ${error?.message}`,
								hasStepChanged$
							);
						}
					);
				},
			});
	}

	/**
	 * Libera el candado cuando no hubo navegación. Sin esto el flujo se queda
	 * bloqueado: _isChangingStep solo se apaga con un NavigationEnd, así que
	 * cualquier salida sin navegar dejaba muertos todos los cambios de paso
	 * posteriores.
	 */
	private abortChangingStep(
		reason: string,
		hasStepChanged$?: Subject<boolean>
	): void {
		console.warn(`[Onboarding] No se cambió de paso: ${reason}`);
		this.changingStepCompleted();
		this.notifyStepChange(hasStepChanged$, false);
	}

	private notifyStepChange(
		hasStepChanged$: Subject<boolean> | undefined,
		hasStepChanged: boolean
	): void {
		hasStepChanged$?.next(hasStepChanged);
		hasStepChanged$?.complete();
	}

	private updateCurrentRoute(path: string) {
		this.store.dispatch(
			RoutingActions.addCurrentRoute({
				currentRoute: path,
			})
		);
	}

	private startChangingStep(): void {
		this._isChangingStep = true;
	}
}
